import {
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { useEffect, useState } from 'react';
import { AttestationChallenge } from '~/components/attestation-challenge/attestation-challenge';
import { useAttestationChallenge } from '~/components/attestation-challenge/use-attestation-challenge';
import { PhoneInput } from '~/components/phone-input';
import { m } from '~/paraglide/messages.js';
import { setLocalStorageItem } from '~/utils/local-storage';
import { useCheckAliases } from './-hooks/use-check-email-availability';
import { useLogin } from './-hooks/use-login';

export const Route = createFileRoute('/auth/login/')({
  validateSearch: (search: Record<string, string>): { redirect?: string } => {
    return {
      redirect: search.redirect,
    };
  },
  component: LoginPage,
});

function getButtonLabel(
  aliasLength: number,
  isSubmitting: boolean,
  isLoading: boolean,
  aliasStatus: string,
  isPhoneAlias: boolean,
) {
  if (isSubmitting || isLoading) return m['auth.login.button_validating']();
  if (aliasLength < 3) return m['auth.login.button_login']();

  switch (aliasStatus) {
    case 'active':
      return m['auth.login.button_login']();
    case 'not_found':
      return m['auth.login.button_signup']();
    case 'not_verified':
      return isPhoneAlias
        ? m['auth.login.button_check_phone']()
        : m['auth.login.button_check_email']();
    default:
      return m['auth.login.button_login']();
  }
}

function LoginPage() {
  const { handleAttestationChallenge } = useAttestationChallenge({
    authFlow: 'login',
  });
  const { handleLogin, isLoading: isSubmitting } = useLogin();
  const {
    alias,
    metaAlias,
    originName,
    onChangeAlias,
    aliasStatus,
    isPhoneAlias,
    isLoading,
  } = useCheckAliases();

  const navigate = useNavigate();

  const location = useLocation();
  const [isPhoneMode, setIsPhoneMode] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem('signup_form_draft');
    const redirect = location.search?.redirect;
    if (redirect) {
      setLocalStorageItem('redirect', redirect);
    }
  }, []);

  const handleAliasChange = (e: any) => {
    const value = e.currentTarget.value;
    const numericValue = value.replace(/\D/g, '');

    if (value === numericValue && numericValue.length > 4) {
      setIsPhoneMode(true);
      const fakeEvent = {
        currentTarget: { value: `+57${numericValue}` },
      } as any;
      onChangeAlias(fakeEvent);

      setTimeout(() => {
        const phoneInput = document.getElementById(
          'phone-number-input',
        ) as HTMLInputElement;
        phoneInput?.focus();
      }, 0);
    } else {
      onChangeAlias(e);
    }
  };

  const handlePhoneValueChange = (value: string) => {
    const COUNTRY_CODE_LENGTHS = [1, 2, 3];
    let phoneNumberPart = value;

    for (const length of COUNTRY_CODE_LENGTHS) {
      const potentialPhone = value.substring(length);
      if (potentialPhone.length > 0) {
        phoneNumberPart = potentialPhone;
        break;
      }
    }

    if (phoneNumberPart.length <= 4) {
      setIsPhoneMode(false);
      const fakeEvent = {
        currentTarget: { value: phoneNumberPart },
      } as any;
      onChangeAlias(fakeEvent);

      setTimeout(() => {
        const aliasInput = document.getElementById(
          'alias-input',
        ) as HTMLInputElement;
        aliasInput?.focus();
      }, 0);
    } else {
      const fakeEvent = {
        currentTarget: { value: `+${value}` },
      } as any;
      onChangeAlias(fakeEvent);
    }
  };

  const navigateToSignup = () => {
    const isEmail = alias.includes('@');
    navigate({
      to: '/auth/signup',
      search: isEmail ? { email: alias } : { phone: alias.replace(/^\+/, '') },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isLoading) return;

    if (aliasStatus === 'active') {
      handleLogin(metaAlias, originName);
    } else if (aliasStatus === 'not_found') {
      navigateToSignup();
    }
  };

  const buttonLabel = getButtonLabel(
    alias.trim().length,
    isSubmitting,
    isLoading,
    aliasStatus,
    isPhoneAlias,
  );

  const isButtonDisabled =
    alias.trim().length < 3 ||
    isLoading ||
    isSubmitting ||
    aliasStatus === 'not_verified';

  return (
    <>
      <AttestationChallenge
        handleAttestationChallenge={handleAttestationChallenge}
      />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold text-primary-900">
              {m['auth.login.title']()}
            </h1>
            <p className="font-normal text-sm text-muted-foreground">
              {m['auth.login.subtitle']()}
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="user"
                  className="text-sm font-medium text-primary-700"
                >
                  {m['auth.login.account_label']()}
                </Label>
              </div>

              <div style={{ display: !isPhoneMode ? 'block' : 'none' }}>
                <Input
                  id="alias-input"
                  type="text"
                  placeholder={m['auth.login.input_placeholder']()}
                  className="w-full"
                  value={alias}
                  onInput={handleAliasChange}
                />
                <p className="text-muted-foreground font-normal text-sm">
                  {m['auth.login.input_hint']()}
                </p>
              </div>
              <div style={{ display: isPhoneMode ? 'block' : 'none' }}>
                <PhoneInput
                  name="phone"
                  placeholder={m['auth.login.phone_placeholder']()}
                  required={false}
                  value={alias.replace(/^\+/, '')}
                  onChange={handlePhoneValueChange}
                  hasError={false}
                />
                <p className="text-muted-foreground font-normal text-sm">
                  {m['auth.login.phone_hint']()}
                </p>
              </div>

              {aliasStatus === 'not_verified' && (
                <p className="text-amber-600 font-normal text-sm">
                  {m['auth.login.not_verified_hint']()}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isButtonDisabled}
            >
              {buttonLabel}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-primary-600">
              {m['auth.login.no_account']()}{' '}
              <Link
                to="/auth/signup"
                className="text-primary-900 hover:text-primary-700 underline font-medium"
              >
                {m['auth.login.signup_link']()}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
